// RTS camera: fixed-yaw, fixed-pitch perspective view looking down-forward the
// way StarCraft does, plus every screen<->world projection the rest of the game
// needs (click targeting, minimap view rectangle, worldToScreen for UI).
//
// Everything in this file is deliberately free of WebGL. It builds a
// THREE.PerspectiveCamera and does matrix maths on it, which works fine in
// Node, so the whole projection/clamping layer is unit-testable headlessly.
//
// AXIS CONVENTION. The simulation is 2D in tiles: (x, y) with y increasing
// "south". Three.js is 3D: we map tile (x, y) -> world (x, elevation, y). So
// the map lies in the XZ plane and Y is up. The camera sits at larger Z (south
// of the focus) and looks north and down, which puts north at the top of the
// screen and makes sim-y increase downward on screen -- the same orientation
// the minimap uses.

import * as THREE from 'three';
import { MAP_W, MAP_H } from '../sim/constants.js';

/** World-units of elevation between low ground and high ground. */
export const TERRAIN_HIGH = 1.7;

export const CAM_PITCH = (55 * Math.PI) / 180;   // degrees below the horizon
export const CAM_FOV = 45;
export const MIN_DIST = 15;
export const MAX_DIST = 78;
export const DEFAULT_DIST = 38;
export const EDGE_PX = 8;                        // pointer edge-scroll band
export const PAN_RATE = 0.85;                    // tiles/sec per unit of camera distance
export const FOCUS_MARGIN = 2;                   // how close the focus may get to the map edge

/**
 * The camera aims at the TERRAIN SURFACE under its focus point, not at a fixed
 * plane. Aiming at a fixed mid-height plane is cheaper and never lurches, but it
 * puts the focus a dozen pixels off screen centre on both terrain levels, and
 * the whole rest of the game is built on the focus being the centre: centerOn()
 * from the minimap, "centre on selection", and the minimap's own view rectangle
 * all quietly disagree with what the player sees.
 *
 * The lurch that a fixed plane avoids is instead handled by gliding the
 * reference height at HEIGHT_FOLLOW world-units/sec, so panning off a plateau
 * slides the view down over ~0.2 s instead of snapping. centerOn() -- a
 * deliberate jump -- snaps it, because there is nothing to smooth into.
 */
export const HEIGHT_FOLLOW = 9.0;

/**
 * The elevation band the rendered terrain surface occupies. viewRect() has to
 * bracket every point screenToWorld() can return, and screenToWorld() picks
 * against real terrain, so the rect is the union of the frustum footprint at the
 * bottom and the top of this band. Measuring on one plane makes the minimap's
 * view box disagree with the screen by ~3 tiles wherever the two levels meet.
 *
 * These are only the DEFAULTS, and they are not the whole story: the rendered
 * surface also includes the rock outcrops on hard blockers and the raised map
 * rim, which reach well above TERRAIN_HIGH. scene.js measures the mesh it
 * actually built and calls setHeightRange() with the real extents, because a
 * band that stops at TERRAIN_HIGH leaves screenToWorld able to answer with
 * points the rect does not contain -- exactly the bug this band exists to fix.
 */
export const GROUND_LO = 0;
export const GROUND_HI = TERRAIN_HIGH;

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
export { clamp };

/** Clamp a camera focus point so the view can never leave the map. */
export function clampFocus(x, y, margin = FOCUS_MARGIN, w = MAP_W, h = MAP_H) {
  return { x: clamp(x, margin, w - margin), y: clamp(y, margin, h - margin) };
}

/**
 * Intersect a ray with the horizontal plane Y = planeY.
 * Returns the parametric distance t, or -1 when the ray runs away from it.
 */
export function rayPlaneT(oy, dy, planeY) {
  if (dy > -1e-7 && dy < 1e-7) return -1;
  const t = (planeY - oy) / dy;
  return t > 0 ? t : -1;
}

/** Shortest-arc angular interpolation, used for facing and for tests. */
export function lerpAngle(a, b, t) {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

/**
 * Read a "keys pressed" container that could be a Set, a Map, a plain object or
 * an array. The input layer is written by a different module, so accept the lot
 * rather than break camera control on a naming mismatch.
 */
export function keyDown(keys, names) {
  if (!keys) return false;
  const hasFn = typeof keys.has === 'function';
  const isArr = Array.isArray(keys);
  for (let i = 0; i < names.length; i++) {
    const n = names[i];
    if (isArr) {
      if (keys.indexOf(n) >= 0) return true;
    } else if (hasFn) {
      if (keys.has(n)) return true;
    } else if (keys[n]) return true;
  }
  return false;
}

const UP_KEYS = ['w', 'W', 'KeyW', 'ArrowUp', 'arrowup', 'Up'];
const DOWN_KEYS = ['s', 'S', 'KeyS', 'ArrowDown', 'arrowdown', 'Down'];
const LEFT_KEYS = ['a', 'A', 'KeyA', 'ArrowLeft', 'arrowleft', 'Left'];
const RIGHT_KEYS = ['d', 'D', 'KeyD', 'ArrowRight', 'arrowright', 'Right'];

/**
 * Normalise the `pointerEdge` argument into { x, y } in [-1, 1]. Accepts
 * { x, y } directions, { left, right, up/top, down/bottom } booleans, or null.
 */
export function normalizeEdge(pe) {
  if (!pe) return null;
  let ex = 0, ey = 0;
  if (typeof pe.x === 'number' && typeof pe.y === 'number' && Math.abs(pe.x) <= 1 && Math.abs(pe.y) <= 1) {
    ex = pe.x; ey = pe.y;
  }
  if (pe.left) ex = -1; if (pe.right) ex = 1;
  if (pe.up || pe.top) ey = -1; if (pe.down || pe.bottom) ey = 1;
  if (typeof pe.dx === 'number') ex = clamp(pe.dx, -1, 1);
  if (typeof pe.dy === 'number') ey = clamp(pe.dy, -1, 1);
  if (ex === 0 && ey === 0) return null;
  return { x: ex, y: ey };
}

const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _target = new THREE.Vector3();
const _hit = { x: 0, y: 0 };
const _screen = { x: 0, y: 0, behind: false };
const _corner = [
  { x: 0, y: 0, x2: 0, y2: 0 }, { x: 0, y: 0, x2: 0, y2: 0 },
  { x: 0, y: 0, x2: 0, y2: 0 }, { x: 0, y: 0, x2: 0, y2: 0 },
];

export class RTSCamera {
  constructor(world) {
    this.world = world || null;
    this.grid = world ? world.grid : null;
    this.mapW = this.grid ? this.grid.w : MAP_W;
    this.mapH = this.grid ? this.grid.h : MAP_H;

    this.vw = 1280;
    this.vh = 720;
    this.pitch = CAM_PITCH;
    this.dist = DEFAULT_DIST;
    this.focus = { x: this.mapW / 2, y: this.mapH / 2 };
    this.groundY = 0;           // elevation the camera is aimed at
    this.loY = GROUND_LO;
    this.hiY = GROUND_HI;

    this.cam = new THREE.PerspectiveCamera(CAM_FOV, this.vw / this.vh, 0.5, 600);

    // Fallback input state. The InputController normally drives update() with
    // its own key set and edge flags; when it does not, these keep the camera
    // usable on its own.
    this._keys = new Set();
    this._edge = null;
    this._pointer = { x: 0, y: 0, inside: false };
    this._drag = null;
    this._wheel = 0;
    this._drivenAt = -1;      // frame stamp of the last external update() call
    this._frame = 0;
    this._listeners = null;
    this.canvas = null;

    // Terrain elevation lookup; scene.js swaps in the real (ramp-aware) one.
    this.heightSampler = (x, y) => {
      if (!this.grid) return 0;
      const xi = x | 0, yi = y | 0;
      if (xi < 0 || yi < 0 || xi >= this.grid.w || yi >= this.grid.h) return 0;
      return this.grid.height[yi * this.grid.w + xi] ? TERRAIN_HIGH : 0;
    };

    this.groundY = this.heightSampler(this.focus.x, this.focus.y);
    this._recompute();
  }

  /**
   * Swap in the real terrain sampler. The reference height is re-snapped, not
   * glided: this runs at construction, before anything has been drawn.
   */
  setHeightSampler(fn) {
    if (typeof fn !== 'function') return;
    this.heightSampler = fn;
    this.groundY = fn(this.focus.x, this.focus.y);
    this._recompute();
  }
  /** Elevation band the surface occupies, for viewRect. */
  setHeightRange(lo, hi) {
    this.loY = Math.min(lo, hi);
    this.hiY = Math.max(lo, hi);
  }
  sampleHeight(x, y) { return this.heightSampler(x, y); }

  setViewport(w, h) {
    this.vw = Math.max(1, w | 0);
    this.vh = Math.max(1, h | 0);
    this.cam.aspect = this.vw / this.vh;
    this.cam.updateProjectionMatrix();
    this._recompute();
  }

  /** Jump the view. Snaps the reference height -- a jump has nothing to glide. */
  centerOn(x, y) {
    const c = clampFocus(x, y, FOCUS_MARGIN, this.mapW, this.mapH);
    this.focus.x = c.x;
    this.focus.y = c.y;
    this.groundY = this.sampleHeight(c.x, c.y);
    this._recompute();
  }

  setZoom(d) { this.dist = clamp(d, MIN_DIST, MAX_DIST); this._recompute(); }
  zoomBy(f) { this.setZoom(this.dist * f); }

  /** Glide the reference height toward the terrain under the focus. */
  _followGround(dt) {
    const want = this.sampleHeight(this.focus.x, this.focus.y);
    const d = want - this.groundY;
    if (d === 0) return;
    const step = HEIGHT_FOLLOW * (dt > 0 ? dt : 0);
    if (step <= 0 || Math.abs(d) <= step) this.groundY = want;
    else this.groundY += d < 0 ? -step : step;
  }

  /** Rebuild the THREE camera transform from focus/dist/pitch. */
  _recompute() {
    const c = clampFocus(this.focus.x, this.focus.y, FOCUS_MARGIN, this.mapW, this.mapH);
    this.focus.x = c.x; this.focus.y = c.y;
    const back = Math.cos(this.pitch) * this.dist;
    const up = Math.sin(this.pitch) * this.dist;
    this.cam.position.set(this.focus.x, this.groundY + up, this.focus.y + back);
    _target.set(this.focus.x, this.groundY, this.focus.y);
    this.cam.up.set(0, 1, 0);
    this.cam.lookAt(_target);
    this.cam.updateMatrix();
    this.cam.updateMatrixWorld(true);
    this.cam.matrixWorldInverse.copy(this.cam.matrixWorld).invert();
  }

  // ---- input plumbing ----------------------------------------------------
  /**
   * Attach the camera's own listeners. The camera owns middle-drag pan and
   * wheel zoom; the input layer should not also bind those or they double up.
   */
  attach(canvas) {
    if (this.canvas || typeof window === 'undefined') return;
    this.canvas = canvas;
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect ? canvas.getBoundingClientRect() : { left: 0, top: 0, width: this.vw, height: this.vh };
      this._pointer.x = e.clientX - r.left;
      this._pointer.y = e.clientY - r.top;
      this._pointer.inside = true;
      if (this._drag) this._dragTo(this._pointer.x, this._pointer.y);
      this._edge = this._edgeFromPointer(e.clientX, e.clientY);
    };
    const onLeave = () => { this._pointer.inside = false; this._edge = null; };
    const onDown = (e) => {
      if (e.button !== 1) return;
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      const sx = e.clientX - r.left, sy = e.clientY - r.top;
      const w = this.screenToWorld(sx, sy);
      this._drag = { wx: w.x, wy: w.y };
      if (canvas.setPointerCapture && e.pointerId !== undefined) { try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ } }
    };
    const onUp = (e) => { if (e.button === 1 || e.type === 'pointercancel') this._drag = null; };
    const onWheel = (e) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
      this._wheel += e.deltaY * unit;
    };
    const onKeyDown = (e) => { this._keys.add(e.key); };
    const onKeyUp = (e) => { this._keys.delete(e.key); };
    const onBlur = () => { this._keys.clear(); this._edge = null; this._drag = null; };
    const onCtx = (e) => e.preventDefault();

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    document.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onCtx);
    this._listeners = { onMove, onDown, onUp, onWheel, onKeyDown, onKeyUp, onBlur, onLeave, onCtx };
  }

  detach() {
    if (!this._listeners || typeof window === 'undefined') return;
    const L = this._listeners;
    window.removeEventListener('pointermove', L.onMove);
    window.removeEventListener('pointerdown', L.onDown);
    window.removeEventListener('pointerup', L.onUp);
    window.removeEventListener('pointercancel', L.onUp);
    window.removeEventListener('blur', L.onBlur);
    window.removeEventListener('keydown', L.onKeyDown);
    window.removeEventListener('keyup', L.onKeyUp);
    document.removeEventListener('pointerleave', L.onLeave);
    if (this.canvas) {
      this.canvas.removeEventListener('wheel', L.onWheel);
      this.canvas.removeEventListener('contextmenu', L.onCtx);
    }
    this._listeners = null;
    this.canvas = null;
  }

  _edgeFromPointer(clientX, clientY) {
    if (typeof window === 'undefined') return null;
    const w = window.innerWidth, h = window.innerHeight;
    let ex = 0, ey = 0;
    if (clientX <= EDGE_PX) ex = -1; else if (clientX >= w - 1 - EDGE_PX) ex = 1;
    if (clientY <= EDGE_PX) ey = -1; else if (clientY >= h - 1 - EDGE_PX) ey = 1;
    return ex || ey ? { x: ex, y: ey } : null;
  }

  /** Middle-drag: keep the world point grabbed on mousedown under the cursor. */
  _dragTo(sx, sy) {
    for (let i = 0; i < 2; i++) {
      const cur = this.screenToWorld(sx, sy);
      const dx = this._drag.wx - cur.x;
      const dy = this._drag.wy - cur.y;
      if (Math.abs(dx) < 1e-4 && Math.abs(dy) < 1e-4) break;
      this.focus.x += dx;
      this.focus.y += dy;
      this._recompute();
    }
    this._followGround(1 / 60);
    this._recompute();
  }

  // ---- per-frame ---------------------------------------------------------
  /**
   * @param dt          seconds since the previous frame
   * @param keys        Set/object/array of currently held keys (optional)
   * @param pointerEdge { x:-1|0|1, y:-1|0|1 } edge-scroll direction (optional)
   */
  update(dt, keys, pointerEdge) {
    this._drivenAt = this._frame;
    this._apply(dt, keys || this._keys, normalizeEdge(pointerEdge) || this._edge);
  }

  /** Called by the renderer; drives the camera itself if nobody else did. */
  tick(dt) {
    if (this._drivenAt !== this._frame) this._apply(dt, this._keys, this._edge);
    this._frame++;
  }

  _apply(dt, keys, edge) {
    if (!(dt > 0)) dt = 0;
    if (dt > 0.1) dt = 0.1;

    if (this._wheel !== 0) {
      this.dist = clamp(this.dist * Math.exp(this._wheel * 0.0011), MIN_DIST, MAX_DIST);
      this._wheel = 0;
    }

    let mx = 0, my = 0;
    if (keyDown(keys, LEFT_KEYS)) mx -= 1;
    if (keyDown(keys, RIGHT_KEYS)) mx += 1;
    if (keyDown(keys, UP_KEYS)) my -= 1;
    if (keyDown(keys, DOWN_KEYS)) my += 1;
    if (edge) { if (edge.x) mx = edge.x; if (edge.y) my = edge.y; }

    if (mx || my) {
      const inv = mx && my ? Math.SQRT1_2 : 1;
      const speed = PAN_RATE * this.dist * dt * inv;
      this.focus.x += mx * speed;
      this.focus.y += my * speed;
      const c = clampFocus(this.focus.x, this.focus.y, FOCUS_MARGIN, this.mapW, this.mapH);
      this.focus.x = c.x; this.focus.y = c.y;
    }
    this._followGround(dt);
    this._recompute();
  }

  // ---- projection --------------------------------------------------------
  /** Build the world-space picking ray for a canvas pixel. */
  _ray(sx, sy) {
    const ndcx = (sx / this.vw) * 2 - 1;
    const ndcy = -(sy / this.vh) * 2 + 1;
    _tmp.set(ndcx, ndcy, 0.5).unproject(this.cam);
    _origin.copy(this.cam.position);
    _dir.copy(_tmp).sub(_origin).normalize();
    return _dir;
  }

  /** Ray/plane hit at a fixed elevation, written into `out`. */
  _groundAt(sx, sy, planeY, out) {
    const d = this._ray(sx, sy);
    let t = rayPlaneT(_origin.y, d.y, planeY);
    if (t < 0) t = this.dist * 6;                     // above the horizon: shoot far
    out.x = _origin.x + d.x * t;
    out.y = _origin.z + d.z * t;
    return out;
  }

  /**
   * Canvas pixel -> tile coordinates, raycast against the terrain at its real
   * elevation. Two refinement passes are enough: the first hit tells us which
   * tile we are over, the second re-intersects at that tile's height.
   */
  screenToWorld(sx, sy) {
    let planeY = this.groundY;
    this._groundAt(sx, sy, planeY, _hit);
    for (let i = 0; i < 3; i++) {
      const h = this.sampleHeight(_hit.x, _hit.y);
      if (Math.abs(h - planeY) < 1e-3) break;
      planeY = h;
      this._groundAt(sx, sy, planeY, _hit);
    }
    return {
      x: clamp(_hit.x, 0, this.mapW),
      y: clamp(_hit.y, 0, this.mapH),
    };
  }

  /**
   * Tile coordinates -> canvas pixels, with `lift` measured above the TERRAIN
   * SURFACE. This is the exact inverse of screenToWorld (which always answers
   * with a point on the surface); worldToScreen's absolute-elevation form is
   * not, and disagrees by TERRAIN_HIGH / tan(pitch) = 1.19 tiles on high ground.
   */
  surfaceToScreen(x, y, lift = 0) {
    return this.worldToScreen(x, y, this.sampleHeight(x, y) + lift);
  }

  /** Tile coordinates (+ ABSOLUTE elevation) -> canvas pixels. */
  worldToScreen(x, y, z = 0) {
    _tmp.set(x, z, y).applyMatrix4(this.cam.matrixWorldInverse);
    const behind = _tmp.z > -this.cam.near;
    _tmp.applyMatrix4(this.cam.projectionMatrix);
    _screen.x = (_tmp.x * 0.5 + 0.5) * this.vw;
    _screen.y = (-_tmp.y * 0.5 + 0.5) * this.vh;
    _screen.behind = behind;
    return { x: _screen.x, y: _screen.y, behind };
  }

  /**
   * One screen corner contributes the ground hit at BOTH ends of the terrain
   * elevation band. The true hit for that pixel is somewhere on the segment
   * between them, and a bounding box that contains both endpoints contains the
   * whole segment -- so the rect is guaranteed to bracket screenToWorld() at
   * every pixel, whatever the terrain under it does.
   */
  _bandCorner(sx, sy, out) {
    const d = this._ray(sx, sy);
    const ox = _origin.x, oy = _origin.y, oz = _origin.z;
    const dx = d.x, dy = d.y, dz = d.z;
    let tLo = rayPlaneT(oy, dy, this.loY);
    if (tLo < 0) tLo = this.dist * 6;
    let tHi = rayPlaneT(oy, dy, this.hiY);
    if (tHi < 0) tHi = this.dist * 6;
    out.x = ox + dx * tLo; out.y = oz + dz * tLo;
    out.x2 = ox + dx * tHi; out.y2 = oz + dz * tHi;
    return out;
  }

  /**
   * The axis-aligned world-tile rectangle currently on screen, measured over the
   * whole terrain elevation band so it agrees with screenToWorld at the corners.
   */
  viewRect() { return this.viewRectInto({ x0: 0, y0: 0, x1: 0, y1: 0 }); }

  /**
   * viewRect() into a caller-owned object. The renderer fits the sun's shadow
   * camera to this every single frame, and the frame path does not allocate.
   */
  viewRectInto(out) {
    this._bandCorner(0, 0, _corner[0]);
    this._bandCorner(this.vw, 0, _corner[1]);
    this._bandCorner(this.vw, this.vh, _corner[2]);
    this._bandCorner(0, this.vh, _corner[3]);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i < 4; i++) {
      const c = _corner[i];
      if (c.x < x0) x0 = c.x;
      if (c.x > x1) x1 = c.x;
      if (c.y < y0) y0 = c.y;
      if (c.y > y1) y1 = c.y;
      if (c.x2 < x0) x0 = c.x2;
      if (c.x2 > x1) x1 = c.x2;
      if (c.y2 < y0) y0 = c.y2;
      if (c.y2 > y1) y1 = c.y2;
    }
    out.x0 = clamp(x0, 0, this.mapW);
    out.y0 = clamp(y0, 0, this.mapH);
    out.x1 = clamp(x1, 0, this.mapW);
    out.y1 = clamp(y1, 0, this.mapH);
    return out;
  }
}
